/**
 * Sign in.
 *
 * The first thing anyone sees, so it is the product and not a form: the rail's
 * own header treatment on the deep-green ground, the wordmark, one use of the
 * gradient, the artifact's card.
 *
 * ── The three refusals are three different screens' worth of meaning ────────
 * A login that says "something went wrong" for all of them is the reason people
 * ring the office. Each is handled on its own terms:
 *
 *   bad credentials  say so plainly, keep the username, clear the password, put
 *                    the cursor back in the password box. Show attempts left
 *                    when the server volunteers it, so the lockout is never a
 *                    surprise.
 *   locked out       say WHEN IT LIFTS — the clock time and the minutes — and
 *                    count it down so the button re-enables itself. A lockout
 *                    with no horizon is what makes somebody phone the office.
 *   unreachable      say that it is RocketRep that did not answer. Reps will
 *                    assume the Aspira platform is down and wait for it; the
 *                    two sign-ins are separate and the screen should say so.
 *
 * `mustChangePassword` is not a refusal — the password works, it just is not
 * theirs yet — so it moves them to the change screen IN THIS APP. We own
 * passwords now; there is nowhere else to send them.
 */

import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { AuthShell, Booting, Message, Submit } from "./chrome";
import { landingAfterSignIn } from "./types";
import type { Lockout } from "./api";

type Problem =
  | { kind: "none" }
  | { kind: "bad"; message: string | null; attemptsLeft: number | null }
  | { kind: "locked"; lockout: Lockout }
  | { kind: "disabled"; message: string | null }
  | { kind: "refused"; message: string }
  | { kind: "unreachable"; message: string };

export default function LoginScreen() {
  const { status, person, mustChangePassword, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem>({ kind: "none" });
  const passwordRef = useRef<HTMLInputElement | null>(null);

  const lockedUntil = problem.kind === "locked" ? problem.lockout.until : null;
  const secondsLeft = useCountdown(lockedUntil);
  const stillLocked = problem.kind === "locked" && (secondsLeft === null || secondsLeft > 0);

  // Where they were heading before they were bounced here.
  const from = readFrom(location.state);

  if (status === "checking") return <Booting />;

  // Already signed in. The change-password screen is the one exception — the
  // session exists but is not usable for anything else yet.
  if (status === "signed-in" && person) {
    return <Navigate to={mustChangePassword ? "/change-password" : landingAfterSignIn(person, from)} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy || stillLocked) return;

    const trimmed = email.trim();
    if (!trimmed || !password) {
      setProblem({
        kind: "bad",
        message: "Enter your username and password.",
        attemptsLeft: null,
      });
      return;
    }

    setBusy(true);
    setProblem({ kind: "none" });
    const out = await login(trimmed, password);
    setBusy(false);

    switch (out.kind) {
      case "ok":
        navigate(landingAfterSignIn(out.person, from), { replace: true });
        return;
      case "must-change-password":
        navigate("/change-password", { replace: true });
        return;
      case "bad-credentials":
        setPassword("");
        setProblem({ kind: "bad", message: out.message, attemptsLeft: out.attemptsLeft });
        passwordRef.current?.focus();
        return;
      case "locked":
        setPassword("");
        setProblem({ kind: "locked", lockout: out.lockout });
        return;
      case "disabled":
        setPassword("");
        setProblem({ kind: "disabled", message: out.message });
        return;
      case "refused":
        setProblem({ kind: "refused", message: out.message });
        return;
      case "unreachable":
        setProblem({ kind: "unreachable", message: out.message });
        return;
    }
  }

  const badField = problem.kind === "bad";

  return (
    <AuthShell
      eyebrow="RocketRep · Aspira"
      title="Sign in"
      sub="Sign in with your RocketRep username."
      foot={
        <>
          Forgot it? Ask your team leader to reset it, or write to{" "}
          <b>support@buildfor.healthcare</b>.
        </>
      }
    >
      <form className="rra-form" onSubmit={onSubmit} noValidate>
        <label className="rra-field">
          <span className="rra-k">Username or email</span>
          <span className="rra-inp-wrap">
            <input
              className={`rra-inp${badField ? " rra-inp-bad" : ""}`}
              type="text"
              name="username"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="firstname.lastname"
              value={email}
              disabled={busy}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
            />
          </span>
        </label>

        <label className="rra-field">
          <span className="rra-k">Password</span>
          <span className="rra-inp-wrap">
            <input
              ref={passwordRef}
              className={`rra-inp rra-inp-eye${badField ? " rra-inp-bad" : ""}`}
              type={reveal ? "text" : "password"}
              name="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              disabled={busy}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="rra-eye"
              onClick={() => setReveal((v) => !v)}
              aria-label={reveal ? "Hide password" : "Show password"}
            >
              {reveal ? "Hide" : "Show"}
            </button>
          </span>
        </label>

        <Submit busy={busy} disabled={stillLocked} busyLabel="Signing in">
          Sign in
        </Submit>
      </form>

      {problem.kind === "bad" ? (
        <Message tone="bad" heading="Wrong username or password">
          {problem.message ?? "Check both and try again."}
          {problem.attemptsLeft !== null ? (
            <span className="rra-when">
              {problem.attemptsLeft === 0
                ? "No attempts left — the next one will lock the account."
                : `${problem.attemptsLeft} ${
                    problem.attemptsLeft === 1 ? "attempt" : "attempts"
                  } left before the account locks.`}
            </span>
          ) : null}
        </Message>
      ) : null}

      {problem.kind === "locked" ? (
        <Message tone="bad" heading={lockoutHeading(problem.lockout)}>
          {problem.lockout.message ??
            "Too many attempts. The lock lifts on its own."}
          <span className="rra-when">{lockoutWhen(problem.lockout, secondsLeft)}</span>
          <div className="rra-note">
            {problem.lockout.scope === "connection"
              ? "Locked for this connection, not your account."
              : "Your team leader can clear it sooner."}
          </div>
        </Message>
      ) : null}

      {problem.kind === "disabled" ? (
        <Message tone="bad" heading="Account switched off">
          {problem.message ??
            "This account has been deactivated."}
          <div className="rra-note">Ask your team leader to switch it back on.</div>
        </Message>
      ) : null}

      {problem.kind === "unreachable" ? (
        <Message tone="bad" heading="Can't reach RocketRep">
          {problem.message}
          <div className="rra-note">Try again in a moment.</div>
        </Message>
      ) : null}

      {problem.kind === "refused" ? (
        <Message tone="bad" heading="The sign-in was refused">
          {problem.message}
        </Message>
      ) : null}
    </AuthShell>
  );
}

/* ── the lockout clock ──────────────────────────────────────────────────── */

/**
 * Seconds until `until`, ticking. `null` when there is no instant to count to —
 * which is a real case: a server may refuse to say when the lock lifts, and the
 * screen then says that rather than inventing a number.
 */
function useCountdown(until: Date | null): number | null {
  const [left, setLeft] = useState<number | null>(() => remaining(until));

  useEffect(() => {
    setLeft(remaining(until));
    if (!until) return undefined;
    const id = window.setInterval(() => setLeft(remaining(until)), 1000);
    return () => window.clearInterval(id);
  }, [until ? until.getTime() : null]);

  return left;
}

function remaining(until: Date | null): number | null {
  if (!until) return null;
  return Math.max(0, Math.round((until.getTime() - Date.now()) / 1000));
}

/** Whose lock it is. Saying "your account" about a shared line is an accusation. */
function lockoutHeading(lockout: Lockout): string {
  if (lockout.scope === "connection") return "Too many attempts from this connection";
  return "This account is locked for now";
}

/**
 * ★ IST, always, and said out loud.
 *
 * The service writes its own lockout message on an IST clock (`istClock` in
 * `src/auth/present.ts`, on the rule that everything in this product is bucketed
 * IST). If this line rendered in the BROWSER's zone, the two would disagree on
 * the same panel the moment anybody opened it from outside India — a manager on
 * a trip, a laptop with the wrong zone set — and a person reading two different
 * times for the same lock believes neither.
 *
 * So the same zone, the same suffix, and the countdown this line adds on top:
 * the horizon is what the server's sentence is missing.
 */
const IST_CLOCK = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

function lockoutWhen(lockout: Lockout, secondsLeft: number | null): string {
  if (!lockout.until) {
    return "The server did not say when it lifts. Try again in a few minutes.";
  }
  if (secondsLeft !== null && secondsLeft <= 0) {
    return "The lock has lifted — try again.";
  }
  const clock = `${IST_CLOCK.format(lockout.until)} IST`;
  if (secondsLeft === null) return `It lifts at ${clock}.`;
  if (secondsLeft < 60) {
    return `It lifts at ${clock} — ${secondsLeft} ${secondsLeft === 1 ? "second" : "seconds"} away.`;
  }
  const mins = Math.ceil(secondsLeft / 60);
  return `It lifts at ${clock} — about ${mins} ${mins === 1 ? "minute" : "minutes"} away.`;
}

/** The path somebody was bounced off, stashed by the guard. */
function readFrom(state: unknown): string | null {
  if (!state || typeof state !== "object") return null;
  const from = (state as { from?: unknown }).from;
  // Only same-site paths are ever honoured — never a full URL from history
  // state, which is attacker-writable.
  return typeof from === "string" && from.startsWith("/") && !from.startsWith("//") ? from : null;
}
